import React from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Activity, TrendingUp } from 'lucide-react';
import { DUCKMON_AGENTS } from '../../types/agent';
import { useRecentSignals } from '../../hooks/useDuckSignals';

const THEME = {
    purple: '#836EF9',
    yellow: '#FFD700',
    green: '#22c55e',
    red: '#ef4444',
    orange: '#FF8C00',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(181, 111, 111, 0.2)',
};

const cardStyle: React.CSSProperties = {
    background: THEME.card,
    border: `1px solid ${THEME.border}`,
    borderRadius: '20px',
    overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
};

const bodyStyle: React.CSSProperties = {
    padding: '1.5rem',
};

const AgentPerformance: React.FC = () => {
    const { signals } = useRecentSignals(50);

    // Signal distribution data
    const signalData = React.useMemo(() => {
        if (!signals || signals.length === 0) return [];
        let buy = 0, sell = 0, hold = 0;
        for (const s of signals) {
            if (s.signalType === 'BUY') buy++;
            else if (s.signalType === 'SELL') sell++;
            else hold++;
        }
        return [
            { name: 'BUY', value: buy, color: THEME.green },
            { name: 'SELL', value: sell, color: THEME.red },
            { name: 'HOLD', value: hold, color: THEME.yellow },
        ];
    }, [signals]);

    // Confidence trend data
    const confidenceTrend = React.useMemo(() => {
        if (!signals || signals.length === 0) return [];
        return signals
            .slice()
            .reverse()
            .slice(0, 20)
            .map((s: { confidence: bigint; signalType: string }, i: number) => ({
                index: i + 1,
                confidence: Number(s.confidence),
                type: s.signalType,
            }));
    }, [signals]);

    // Agent activity - count signals from the core agents
    const agentActivity = React.useMemo(() => {
        const coreAgents = DUCKMON_AGENTS.filter(a => a.category === 'core');
        const signalCount = signals?.length || 0;
        return coreAgents.map(agent => ({
            name: agent.name.replace(/ v[\d.]+/, ''),
            signals: signalCount > 0 ? Math.ceil(signalCount / coreAgents.length) : 0,
            onChain: agent.onChainEnabled,
            icon: agent.icon,
        }));
    }, [signals]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Activity size={24} color={THEME.purple} />
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', margin: 0 }}>Agent Performance</h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Signal Distribution */}
                <div style={cardStyle}>
                    <div style={headerStyle}>
                        <TrendingUp size={18} color={THEME.green} />
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Signal Distribution</h3>
                    </div>
                    <div style={bodyStyle}>
                        {signalData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={signalData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                    <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                                    <YAxis stroke="#6b7280" fontSize={12} />
                                    <Tooltip
                                        contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(131,110,249,0.3)', borderRadius: '8px' }}
                                        labelStyle={{ color: '#fff' }}
                                    />
                                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                                        {signalData.map((entry, i) => (
                                            <Cell key={i} fill={entry.color} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem 0' }}>No signal data yet</p>
                        )}
                    </div>
                </div>

                {/* Confidence Trend */}
                <div style={cardStyle}>
                    <div style={headerStyle}>
                        <Activity size={18} color={THEME.purple} />
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Confidence Trend</h3>
                    </div>
                    <div style={bodyStyle}>
                        {confidenceTrend.length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={confidenceTrend}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                    <XAxis dataKey="index" stroke="#6b7280" fontSize={12} />
                                    <YAxis domain={[0, 100]} stroke="#6b7280" fontSize={12} />
                                    <Tooltip
                                        contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(131,110,249,0.3)', borderRadius: '8px' }}
                                        labelStyle={{ color: '#fff' }}
                                    />
                                    <Line type="monotone" dataKey="confidence" stroke={THEME.purple} strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem 0' }}>No confidence data yet</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Agent Activity Table */}
            <div style={cardStyle}>
                <div style={headerStyle}>
                    <Activity size={18} color={THEME.yellow} />
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#fff', margin: 0, textTransform: 'uppercase', letterSpacing: '0.02em' }}>Agent Activity</h3>
                </div>
                <div style={bodyStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {agentActivity.map((agent, i) => (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0.75rem', background: 'rgba(0,0,0,0.3)', borderRadius: '12px',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <span style={{ fontSize: '1.25rem' }}>{agent.icon}</span>
                                    <span style={{ color: '#fff', fontWeight: 600 }}>{agent.name}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{agent.signals} signals</span>
                                    <span style={{
                                        padding: '0.25rem 0.5rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 600,
                                        background: agent.onChain ? `${THEME.green}20` : `${THEME.yellow}20`,
                                        color: agent.onChain ? THEME.green : THEME.yellow,
                                    }}>
                                        {agent.onChain ? 'ON-CHAIN' : 'OFF-CHAIN'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgentPerformance;
